import { memo } from 'react';
import { AlertCircle, MapPin } from 'lucide-react';
import type { Site } from '../hooks/useSites';
import type { FleetLiveEntry } from '../hooks/useFleetTelemetry';
import type { GeneratorRow } from '../types/project.types';
import { PulseDot } from './site-card/PulseDot';
import { GenTile } from './site-card/GenTile';
import { BessTile } from './site-card/BessTile';
import { CommissioningPanel } from './site-card/CommissioningPanel';
import {
  computeGenStats,
  deriveCardState,
  deriveTelemetryPresence,
  formatMw,
  pickNum,
} from './site-card/derive';

interface SiteCardProps {
  site: Site;
  live?: FleetLiveEntry;
  nowMs?: number;
  onClick?: (siteId: string) => void;
}

// Live, asset-adaptive fleet card (#565). Structure (identity, alarms,
// location) rides GET /v1/sites; numbers ride the telemetry:batch
// site_summary WebSocket payload. All display values are live analogs —
// nameplate metadata is used only for thresholds and asset presence.
export const SiteCard = memo(function SiteCard({ site, live, nowMs = Date.now(), onClick }: SiteCardProps) {
  const state = deriveCardState(site, live, nowMs);
  const dimmed = state === 'stale' || state === 'offline';
  const summary = live?.summary;

  const gens = Array.isArray(summary?.generators)
    ? (summary.generators as GeneratorRow[])
    : [];
  const hasGens = (site.generator_count ?? 0) > 0 || gens.length > 0;

  const socPct = pickNum(summary, 'average_soc', 'average_soc_percent');
  const bessPowerKw = pickNum(summary, 'bess_power_kw');
  const hasBess =
    site.bess_power_mw != null ||
    site.bess_energy_mwh != null ||
    socPct != null ||
    bessPowerKw != null;

  const perGenKw =
    site.gen_capacity_kw != null && (site.generator_count ?? 0) > 0
      ? site.gen_capacity_kw / (site.generator_count as number)
      : null;
  const genStats = computeGenStats(gens, perGenKw);
  const genCounts = gens.length > 0 ? { running: genStats.running, total: genStats.total } : null;

  const outputKw = pickNum(summary, 'total_generation_kw');
  const capOnlineKw = pickNum(summary, 'capacity_online_kw');

  const bessOnlyHero = !hasGens && hasBess;
  const heroKw = bessOnlyHero ? bessPowerKw : outputKw;
  const utilRawPct =
    !bessOnlyHero && heroKw != null && capOnlineKw != null && capOnlineKw > 0
      ? (heroKw / capOnlineKw) * 100
      : null;

  const critN = site.alarms_critical_unacked ?? 0;
  const warnN = site.alarms_warning_unacked ?? 0;
  const hasAlarmData =
    site.alarms_critical_unacked != null && site.alarms_warning_unacked != null;

  return (
    <div
      onClick={() => onClick?.(site.site_id)}
      className="cursor-pointer rounded-2xl bg-ems-bg-card border border-ems-border hover:bg-ems-bg-card-hover hover:border-ems-border-bright transition-all duration-200 p-5"
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-ems-text-primary">{site.name}</h3>
          <p className="font-mono text-xs text-ems-text-muted">{site.site_code}</p>
        </div>
        <PulseDot state={state} />
      </div>

      {state === 'commissioning' ? (
        <CommissioningPanel site={site} telemetry={deriveTelemetryPresence(live, nowMs)} />
      ) : (
        <>
          {/* Hero: live output vs capacity online now (never nameplate) */}
          <div className={`mt-4 ${dimmed ? 'opacity-50' : ''}`}>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-ems-text-muted">
              {bessOnlyHero ? 'BESS Power' : 'Site Output'}
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-3xl font-bold text-ems-text-primary">
                {heroKw != null ? formatMw(heroKw) : '—'}
              </span>
              <span className="text-sm text-ems-text-secondary">MW</span>
              {bessOnlyHero
                ? socPct != null && (
                    <span className="ml-auto text-xs text-ems-text-muted">
                      SOC <span className="font-mono text-ems-blue">{Math.round(socPct)}%</span>
                    </span>
                  )
                : capOnlineKw != null && (
                    <span className="ml-auto text-xs text-ems-text-muted">
                      of {formatMw(capOnlineKw)} MW online
                    </span>
                  )}
            </div>
            {utilRawPct != null && (
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-ems-bg">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    utilRawPct > 100 ? 'bg-ems-red' : utilRawPct > 80 ? 'bg-ems-amber' : 'bg-ems-green'
                  }`}
                  style={{ width: `${Math.min(Math.max(utilRawPct, 0), 100)}%` }}
                />
              </div>
            )}
          </div>

          {/* Adaptive tiles — only asset classes the site actually has */}
          {(hasGens || (hasBess && !bessOnlyHero)) && (
            <div
              className={`mt-4 grid gap-2.5 ${
                hasGens && hasBess && !bessOnlyHero ? 'grid-cols-2' : 'grid-cols-1'
              }`}
            >
              {hasGens && (
                <GenTile counts={genCounts} avgLoadPct={genStats.avgLoadPct} dimmed={dimmed} />
              )}
              {hasBess && !bessOnlyHero && (
                <BessTile socPct={socPct} bessPowerKw={bessPowerKw} dimmed={dimmed} />
              )}
            </div>
          )}
        </>
      )}

      {/* Footer: unacked-only severity chips + real location */}
      <div className="mt-4 flex items-center justify-between border-t border-ems-border pt-3">
        <div className="flex items-center gap-1.5">
          {!hasAlarmData ? (
            <span className="text-xs text-ems-text-muted">—</span>
          ) : critN === 0 && warnN === 0 ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-ems-border-bright bg-ems-green/10 px-2 py-0.5 text-[10px] font-bold text-ems-green">
              <AlertCircle className="h-3 w-3" /> CLEAR
            </span>
          ) : (
            <>
              {critN > 0 && (
                <span className="inline-flex items-center rounded-full border border-ems-red/35 bg-ems-red/15 px-2 py-0.5 text-[10px] font-bold text-ems-red">
                  {critN} CRIT
                </span>
              )}
              {warnN > 0 && (
                <span className="inline-flex items-center rounded-full border border-ems-amber/30 bg-ems-amber/10 px-2 py-0.5 text-[10px] font-bold text-ems-amber">
                  {warnN} WARN
                </span>
              )}
            </>
          )}
        </div>
        {site.location_label && (
          <div className="flex items-center gap-1 text-ems-text-muted">
            <MapPin className="h-3 w-3" />
            <span className="text-xs">{site.location_label}</span>
          </div>
        )}
      </div>
    </div>
  );
});
